import type { MultipleChoiceQuestion, Question, TextQuestion } from '@shared/domain/form';
import type { EntryStrategyReview } from '@shared/domain/review';
import type { LegalCheckFlag } from '../../legal-check/types';
import { Button } from '@mui/material';
import { QuestionType } from '@shared/domain/form';
import { ReviewStep } from '@shared/domain/review';
import React from 'react';
import { ENTRY_STRATEGY_FORM_FIELDS } from '../../data';
import { shouldShowQuestion } from '../../helpers/ReviewFormHelpers';
import { getReviewStepName } from '../../helpers/ReviewStepNames';
import { useReviewForm } from '../../hooks/useReviewForm';
import MultipleChoiceReviewQuestion from '../question_types/ReviewMultipleChoiceQuestion';
import ReviewTextArea from '../question_types/ReviewTextQuestion';

import '../../styles/review-form.scss';

export interface EntryStrategyReviewProps {
  submitReviewForm?: (formFields: EntryStrategyReview, reviewStep: ReviewStep, isDraft?: boolean) => void;
  editMode?: boolean;
  defaultValues?: Partial<EntryStrategyReview> & { id?: number };
  handleEditSuccess?: () => void;
  seedLegalCheckFlags?: Record<string, LegalCheckFlag[]>;
}

// Multi-choice + any text follow-up that's conditional on a multi-choice
// answer (e.g. "Please describe the 'Other' channel"). They sit next to
// their trigger in the quick-facts list rather than dropping into the
// long-form grid below.
const isQuickFactQuestion = (q: Question): boolean =>
  q.type === QuestionType.MULTIPLE_CHOICE || (q.type === QuestionType.TEXT && !!q.conditionallyShow);

const isLongFormQuestion = (q: Question): q is TextQuestion => q.type === QuestionType.TEXT && !q.conditionallyShow;

function EntryStrategyReviewForm(props: EntryStrategyReviewProps) {
  const { submitReviewForm, editMode, defaultValues, handleEditSuccess, seedLegalCheckFlags } = props;

  const {
    formFields,
    extendFormFields,
    errors,
    validateField,
    handleFormFieldChange,
    handleSubmit,
    handleEdit,
    getLegalCheckBindings,
  } = useReviewForm<EntryStrategyReview>({
    reviewStep: ReviewStep.ENTRY_STRATEGY,
    formFields: ENTRY_STRATEGY_FORM_FIELDS,
    editMode,
    defaultValues,
    onSubmit: submitReviewForm,
    onEditSuccess: handleEditSuccess,
    seedLegalCheckFlags,
  });

  const quickFacts = ENTRY_STRATEGY_FORM_FIELDS.filter(isQuickFactQuestion);
  const longForm = ENTRY_STRATEGY_FORM_FIELDS.filter(isLongFormQuestion);

  return (
    <div className="review-page">
      <div data-testid="entry-strategy-review-step-content-anchor" />

      <header className="review-page__header">
        <p className="review-page__eyebrow">Step 2 · Entry strategy</p>
        <h1 className="review-page__title">
          {editMode ? 'Edit your entry strategy' : 'How did this account get on the map?'}
        </h1>
        <p className="review-page__subtitle">
          Walk through the moment you got attention, the channel that broke through, and what you'd do differently next
          time.
        </p>
      </header>

      <form>
        {quickFacts.length > 0 && (
          <section className="review-section">
            <header className="review-section__heading">
              <h2 className="review-section__title">Quick facts</h2>
              <p className="review-section__hint">Source, channel, and any clarifying details.</p>
            </header>
            <div className="review-mcq-list">
              {quickFacts.map((question) => {
                if (!shouldShowQuestion(question, formFields)) return null;

                const isConditional =
                  !!question.conditionallyShow ||
                  (Array.isArray(question.conditionalShowRules) && question.conditionalShowRules.length > 0);

                if (question.type === QuestionType.MULTIPLE_CHOICE) {
                  const mc = question as MultipleChoiceQuestion;
                  return (
                    <MultipleChoiceReviewQuestion
                      key={mc.name}
                      question={mc}
                      formFields={formFields}
                      handleFormFieldChange={(e) => {
                        extendFormFields({ [mc.name]: e.currentTarget.value });
                        validateField(mc.name, e.currentTarget.value);
                      }}
                      errors={errors}
                      isNarrow={mc.options.length > 8}
                      isConditional={isConditional}
                    />
                  );
                }

                const text = question as TextQuestion;
                return (
                  <ReviewTextArea
                    key={text.name}
                    question={text}
                    formFields={formFields}
                    handleFormFieldChange={handleFormFieldChange}
                    errors={errors}
                    helperText={text.helperText}
                    legalCheck={getLegalCheckBindings(text.name)}
                    isConditional={isConditional}
                  />
                );
              })}
            </div>
          </section>
        )}

        {longForm.length > 0 && (
          <section className="review-section">
            <header className="review-section__heading">
              <h2 className="review-section__title">Your playbook</h2>
              <p className="review-section__hint">
                The pain points, the proof, and the advice you'd hand to the next AE.
              </p>
            </header>
            <div className="review-text-grid">
              {longForm.map((question, i) => (
                <ReviewTextArea
                  key={question.name}
                  question={question}
                  formFields={formFields}
                  handleFormFieldChange={handleFormFieldChange}
                  errors={errors}
                  includeTextLimitValidation
                  index={i}
                  helperText={question.helperText}
                  legalCheck={getLegalCheckBindings(question.name)}
                />
              ))}
            </div>
          </section>
        )}
      </form>

      <div className="review-save-bar">
        <Button
          variant="contained"
          className="review-save-bar__button"
          data-testid="review-save-button"
          onClick={editMode ? handleEdit : handleSubmit}
        >
          {editMode ? `Save ${getReviewStepName(ReviewStep.ENTRY_STRATEGY)} changes` : 'Continue to Purchase Details'}
        </Button>
      </div>
    </div>
  );
}

export default EntryStrategyReviewForm;
